import React, { FC, useContext, useEffect, useState } from "react";
import { List, ListItem, ListItemText, IconButton, Typography, Box, Button, Divider, styled } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SimpleModal from "common/components/dialogs/SimpleModal";
import { LanguageContext } from "common/contexts/LanguageContext";
import theme from "common/theme/theme";
import DialogTextField from "common/components/dialogs/DialogTextField";
import ConfirmationModal from "common/components/dialogs/ConfirmationModal";
import useNotificationStore from "common/store/NotificationStore";
import useSettingsStore from "common/store/SettingsStore";
import { useAuthContext } from "common/contexts/AuthContext";
import useUserStore, { IProjectShare, ISharedUser } from "common/store/UserStore";

interface IProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  projectID: number;
}

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  margin: 'auto',
  color: theme.palette.primary.dark,
  marginRight: "-12px",
  backgroundColor: 'transparent',
  '&:hover, &:focus, &:active': {
    color: theme.palette.primary.light,
    backgroundColor: 'transparent',
  },
  '& .MuiTouchRipple-root': {
    display: 'none',
  },
}));

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const ShareProjectDialog: FC<IProps> = ({ isOpen, setOpen, projectID }) => {
  const [emailAddress, setEmailAddress] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [projectShare, setProjectShare] = useState<IProjectShare>({ projectID: -1, users: [] });
  const [userToDelete, setUserToDelete] = useState<ISharedUser | null>(null);

  const { getString } = useContext(LanguageContext);
  const { enqueueNotification } = useNotificationStore();
  const { fetchProjectShare, shareProject, unshareProject, isBusy: isUserStoreBusy } = useUserStore();
  const { settings } = useSettingsStore();
  const { getAccessToken } = useAuthContext();

  const isValidEmail = emailRegex.test(emailAddress);

  useEffect(() => {
    if (projectID !== projectShare.projectID && !isUserStoreBusy) {
      (async () => {
        const response = await fetchProjectShare(settings.apiBaseUrl, getAccessToken, projectID);
        if (!response.succeeded) {
          enqueueNotification("load_project_share_failure", getString("msg_fetch_share_failed"), "error");
          return;
        }
        if (response.projectShare) {
          setProjectShare(response.projectShare);
        }
      })();
    }
  }, [
    fetchProjectShare,
    projectShare,
    projectID,
    settings.apiBaseUrl,
    getAccessToken,
    enqueueNotification,
    getString,
    isUserStoreBusy,
  ]);

  useEffect(() => {
    if (isOpen) {
      setEmailAddress("");
    }
  }, [isOpen]);

  const handleDelete = (user: ISharedUser) => {
    setUserToDelete(user);
    setIsConfirmOpen(true);
  };

  const handleUnshareConfirmation = async () => {
    setIsConfirmOpen(false);
    const response = await unshareProject(
      settings.apiBaseUrl,
      getAccessToken,
      projectID,
      userToDelete!.userID,
      userToDelete!.rowVersion,
    );
    if (!response.succeeded) {
      enqueueNotification("unshare_project_failure", getString("msg_unshare_proj_failed"), "error");
      return;
    }
    setProjectShare((prevShare) => ({
      projectID: prevShare.projectID,
      users: prevShare.users.filter((u) => u.userID !== userToDelete!.userID),
    }));
  };

  const handleEmailAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(event.target.value);
  };

  const handleShareProject = async () => {
    const response = await shareProject(settings.apiBaseUrl, getAccessToken, projectID, emailAddress);
    if (!response.succeeded) {
      enqueueNotification("share_project_failure", getString("msg_share_proj_failed"), "error");
      return;
    }
    setProjectShare((prevShare) => ({ projectID: prevShare.projectID, users: [...prevShare.users, response.user!] }));
    setEmailAddress("");
  };

  return (
    <SimpleModal title={getString("dlg_share_prj_title")} maxWidth="md" isOpen={isOpen} setOpen={setOpen}>
      <Box>
        {projectShare?.users.length > 0 ? (
          <Box sx={{ width: "40%", mt: 2, mb: 4 }}>
            <Typography variant="dialoglabel">{getString("dlg_share_prj_shared_to_lbl")}</Typography>
            <Box sx={{ border: "1px solid gray" }}>
              <List sx={{ maxHeight: 175, overflow: "auto" }}>
                {projectShare.users.map((item, index) => (
                  <ListItem
                    key={item.userID}
                    secondaryAction={
                      <StyledIconButton edge="end" aria-label="delete" onClick={() => handleDelete(item)}>
                        <DeleteIcon />
                      </StyledIconButton>
                    }
                    sx={{ py: 0 }}
                  >
                    <ListItemText primary={item.nameIdentifier} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        ) : (
          <div style={{ marginTop: "20px" }}></div>
        )}

        <Typography variant="body1">{getString("dlg_share_prj_new_share_lbl")}</Typography>
        <Divider sx={{ mb: 2, backgroundColor: theme.palette.primary.dark }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DialogTextField
              label={getString("dlg_share_prj_email_lbl")}
              size="small"
              variant="filled"
              value={emailAddress}
              onChange={handleEmailAddressChange}
              sx={{ width: "40%" }}
            />
            <Button variant="contained" sx={{ mt: "16px" }} disabled={!isValidEmail} onClick={handleShareProject}>
              {getString("cmn_add")}
            </Button>
          </Box>
          <Typography paragraph variant="body1">
            {getString("dlg_share_prj_dir_1")}
          </Typography>
          <Typography paragraph variant="body1">
            {getString("dlg_share_prj_dir_2")}
          </Typography>
          <Typography paragraph variant="body1">
            {getString("dlg_share_prj_dir_3")}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
        <Button variant="contained" onClick={() => setOpen(false)}>
          {getString("cmn_ok")}
        </Button>
      </Box>
      <ConfirmationModal
        isOpen={isConfirmOpen}
        handleClose={() => {
          setIsConfirmOpen(false);
        }}
        handleConfirm={handleUnshareConfirmation}
        title={getString("dlg_unshare_confirm_title")}
        maxWidth="sm"
        hideDivider={true}
      >
        <Typography variant="subtitle1" sx={{ pt: "24px" }}>
          {getString("dlg_unshare_confirm_body")}
        </Typography>
      </ConfirmationModal>
    </SimpleModal>
  );
};

export default ShareProjectDialog;
